import { ImageWithFallback } from "@/components/image/ImageWithFallback";
import { isNonNullable } from "@/types/isNonNullable";
import { isNullable } from "@/types/isNullable";
import { type KnockoutTreeNode } from "@motain/xpa-proto-files-web/lib/types/kotree";
import Link from "next/link";
import type { FC } from "react";
import styles from "./KoTreeNode.module.scss";
import { MatchPeriod } from "@motain/xpa-proto-files-web/lib/types/match_card";
import { WarningIcon } from "@/components/warning-icon/WarningIcon";
import classNames from "classnames";
import { trackingService } from "@/modules/analytics";
import { EventType } from "@motain/xpa-proto-files-web/lib/types/tracking";
import { penaltiesSplit } from "@/xpa-components/ko-tree/ko-tree-node/penaltiesSplit";

export const KoTreeNode: FC<KnockoutTreeNode> = (props) => {
  const {
    firstTeam,
    secondTeam,
    modal,
    firstTeamScore,
    secondTeamScore,
    periodFormatted,
    period,
    kickoffTimeFormatted,
    kickoffTime,
    link,
  } = props;
  const Tag = isNullable(modal) && isNonNullable(link) ? Link : "div";
  const isLive =
    period === MatchPeriod.FIRST_HALF ||
    period === MatchPeriod.SECOND_HALF ||
    period === MatchPeriod.EXTRA_FIRST_HALF ||
    period === MatchPeriod.EXTRA_SECOND_HALF ||
    period === MatchPeriod.HALF_TIME ||
    period === MatchPeriod.PENALTIES;

  const isFinished =
    period === MatchPeriod.FULL_TIME ||
    period === MatchPeriod.FULL_TIME_PENALTIES ||
    period === MatchPeriod.RESULTS_AFTER_FULL_TIME;

  const buildLeg = () => {
    switch (period) {
      case MatchPeriod.PRE_MATCH:
        return (
          <p className={classNames("text-5", styles.date)}>
            <span> {periodFormatted} </span>
            <time className={styles.formattedDate} dateTime={kickoffTime}>
              {kickoffTimeFormatted}
            </time>
          </p>
        );
      case MatchPeriod.FULL_TIME:
      case MatchPeriod.RESULTS_AFTER_FULL_TIME:
        return (
          <p className={classNames("title-7-medium", styles.columns)}>
            <span>{firstTeamScore}</span>
            <span>-</span>
            <span>{secondTeamScore}</span>
          </p>
        );
      case MatchPeriod.FULL_TIME_PENALTIES:
        return (
          <p className={classNames("title-7-medium", styles.columns)}>
            <span className={styles.penalties}>
              <div className={styles.penaltiesColor}>
                {penaltiesSplit(firstTeamScore)[0]}
              </div>
              {penaltiesSplit(firstTeamScore)[1]}
            </span>
            <span>-</span>
            <span className={styles.penalties}>
              <div className={styles.penaltiesColor}>
                {penaltiesSplit(secondTeamScore)[0]}
              </div>
              {penaltiesSplit(secondTeamScore)[1]}
            </span>
          </p>
        );
      case MatchPeriod.HALF_TIME:
      case MatchPeriod.EXTRA_SECOND_HALF:
      case MatchPeriod.EXTRA_FIRST_HALF:
      case MatchPeriod.SECOND_HALF:
      case MatchPeriod.FIRST_HALF:
        return (
          <div className={styles.ellipsis}>
            <p className={classNames("title-7-medium", styles.columns)}>
              <span>{firstTeamScore}</span>
              <span>-</span>
              <span>{secondTeamScore}</span>
            </p>
            <p className={classNames("text-5", styles.magenta)}>
              {periodFormatted}
            </p>
          </div>
        );
      case MatchPeriod.PENALTIES:
        return (
          <div className={styles.ellipsis}>
            <p className={classNames("title-7-medium", styles.columns)}>
              <span className={styles.penalties}>
                <div className={styles.penaltiesColor}>
                  {penaltiesSplit(firstTeamScore)[0]}
                </div>
                {penaltiesSplit(firstTeamScore)[1]}
              </span>
              <span>-</span>
              <span className={styles.penalties}>
                <div className={styles.penaltiesColor}>
                  {penaltiesSplit(secondTeamScore)[0]}
                </div>
                {penaltiesSplit(secondTeamScore)[1]}
              </span>
            </p>
            <p className={classNames("text-5", styles.magenta)}>
              {periodFormatted}
            </p>
          </div>
        );
      case MatchPeriod.POSTPONED:
      case MatchPeriod.ABANDONED:
      case MatchPeriod.INTERRUPT:
        return (
          <p className={classNames("text-5", styles.alert)}>
            <WarningIcon testId="warning-icon" className={styles.colored} />
            <span className={styles.colored}>{periodFormatted}</span>
          </p>
        );
      default:
        return null;
    }
  };

  return (
    <Tag
      className={classNames(
        styles.container,
        isLive && styles.live,
        isFinished && styles.finished,
      )}
      href={link?.urlPath ?? ""}
      onClick={() => {
        if (isNonNullable(link)) {
          trackingService.sendXpaTracking(
            link.trackingEvents,
            EventType.EVENT_CLICK,
          );
        }
      }}
    >
      <div className={styles.teamColumn}>
        {isNonNullable(firstTeam) && isNonNullable(firstTeam.logo) && (
          <ImageWithFallback
            className={styles.team}
            alt={firstTeam.logo.alt}
            src={firstTeam.logo.path}
            width={32}
            height={32}
          />
        )}
        {isNonNullable(secondTeam) && isNonNullable(secondTeam.logo) && (
          <ImageWithFallback
            className={styles.team}
            alt={secondTeam.logo.alt}
            src={secondTeam.logo.path}
            width={32}
            height={32}
          />
        )}
      </div>
      {buildLeg()}
    </Tag>
  );
};
